const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;
const JPEG_PREFIX = [0xff, 0xd8, 0xff] as const;
const GIF87A = 'GIF87a';
const GIF89A = 'GIF89a';
const RIFF = 'RIFF';
const WEBP = 'WEBP';
const FTYP = 'ftyp';

type DetectedImageMime =
  | 'image/png'
  | 'image/jpeg'
  | 'image/webp'
  | 'image/gif'
  | 'image/avif'
  | 'image/heic'
  | 'image/heif'
  | 'image/svg+xml';

const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx']);
const HEIF_BRANDS = new Set(['mif1', 'msf1', 'heif', 'heim', 'heis']);
const AVIF_BRANDS = new Set(['avif', 'avis']);

function readAscii(bytes: Uint8Array, start: number, length: number): string {
  if (bytes.length < start + length) return '';
  let out = '';
  for (let i = start; i < start + length; i += 1) {
    out += String.fromCharCode(bytes[i]);
  }
  return out;
}

function startsWith(bytes: Uint8Array, signature: readonly number[]): boolean {
  if (bytes.length < signature.length) return false;
  return signature.every((value, index) => bytes[index] === value);
}

function detectSvg(bytes: Uint8Array): boolean {
  // SVG is text; inspect first 512 bytes for a leading "<svg" tag.
  const prefix = new TextDecoder('utf-8', { fatal: false })
    .decode(bytes.slice(0, 512))
    .trimStart()
    .toLowerCase();

  if (prefix.startsWith('<svg')) return true;
  if (prefix.startsWith('<?xml')) return prefix.includes('<svg');
  return false;
}

export function detectImageMimeFromMagicBytes(bytes: Uint8Array): DetectedImageMime | null {
  if (startsWith(bytes, PNG_SIGNATURE)) return 'image/png';
  if (startsWith(bytes, JPEG_PREFIX)) return 'image/jpeg';

  const gifHeader = readAscii(bytes, 0, 6);
  if (gifHeader === GIF87A || gifHeader === GIF89A) return 'image/gif';

  // WEBP container: RIFF....WEBP
  if (readAscii(bytes, 0, 4) === RIFF && readAscii(bytes, 8, 4) === WEBP) {
    return 'image/webp';
  }

  // ISO BMFF image families: ....ftyp<brand>
  if (readAscii(bytes, 4, 4) === FTYP) {
    const brand = readAscii(bytes, 8, 4).toLowerCase();
    if (AVIF_BRANDS.has(brand)) return 'image/avif';
    if (HEIC_BRANDS.has(brand)) return 'image/heic';
    if (HEIF_BRANDS.has(brand)) return 'image/heif';
  }

  if (detectSvg(bytes)) return 'image/svg+xml';
  return null;
}

export function getFileExtension(name: string): string {
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === name.length - 1) return '';
  return name.slice(lastDot + 1).toLowerCase();
}

export function toHexPrefix(bytes: Uint8Array, length = 8): string {
  return Array.from(bytes.slice(0, length))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
